import { cva, type VariantProps } from 'class-variance-authority'
import { type LucideIcon, Minus, TrendingDown, TrendingUp } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { Icon } from '../atoms/Icon'
import { IconCircle } from '../atoms/IconCircle'

/**
 * StatCard — dashboard KPI card. Pencil konLD / yKF4o / c6tQN.
 */
export const statCardVariants = cva(
  'rounded-lg bg-card border border-ia-border p-4 flex flex-col gap-3 transition-colors hover:border-ia-border-strong',
  {
    variants: {
      tone: {
        default: '',
        warning: 'border-ia-warning/30',
        error: 'border-ia-error/30',
      },
    },
    defaultVariants: { tone: 'default' },
  },
)

export interface StatCardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof statCardVariants> {
  label: React.ReactNode
  value: React.ReactNode
  icon?: LucideIcon
  delta?: { value: number; direction: 'up' | 'down' | 'flat'; label?: string }
  loading?: boolean
  children?: React.ReactNode
}

export const StatCard = React.forwardRef<HTMLDivElement, StatCardProps>(
  ({ className, tone, label, value, icon, delta, loading, children, ...props }, ref) => {
    if (loading) {
      return (
        <div ref={ref} className={cn(statCardVariants({ tone }), className)} {...props}>
          <div className="h-4 w-24 rounded bg-ia-elevated animate-pulse" />
          <div className="h-8 w-16 rounded bg-ia-elevated animate-pulse" />
        </div>
      )
    }
    return (
      <div ref={ref} className={cn(statCardVariants({ tone }), className)} {...props}>
        <div className="flex items-center justify-between">
          <div className="text-overline font-mono uppercase tracking-wider text-ia-text-tertiary">
            {label}
          </div>
          {icon && (
            <IconCircle
              icon={icon}
              tone={tone === 'warning' ? 'warning' : tone === 'error' ? 'error' : 'brand'}
              size="sm"
            />
          )}
        </div>
        <div className="text-display-md font-bold text-ia-text tabular-nums">{value}</div>
        {delta && (
          <div
            className={cn(
              'flex items-center gap-1 text-caption font-medium',
              delta.direction === 'up' && 'text-ia-success',
              delta.direction === 'down' && 'text-ia-error',
              delta.direction === 'flat' && 'text-ia-text-tertiary',
            )}
          >
            <Icon
              icon={
                delta.direction === 'up'
                  ? TrendingUp
                  : delta.direction === 'down'
                    ? TrendingDown
                    : Minus
              }
              size="xs"
            />
            <span>
              {delta.value > 0 ? '+' : ''}
              {delta.value}%
            </span>
            {delta.label && <span className="text-ia-text-tertiary">{delta.label}</span>}
          </div>
        )}
        {children}
      </div>
    )
  },
)
StatCard.displayName = 'StatCard'
